import { type IssueDetail, ISSUE_TEXT_MAX_LENGTH } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';

import { InlineTextEditor } from './InlineTextEditor.js';
import { IssueText } from './IssueText.js';
import type { SaveIssueFields } from './useIssueFieldSave.js';

/**
 * 中身3項目の1つ（見出しの文言キー・項目名の文言キー・文と印の保存先）。
 * @graph-connects none
 */
const CONTENT_ITEMS = [
  {
    key: 'what',
    headingKey: 'issueMgmt.content.what',
    fieldKey: 'issueMgmt.field.what',
    textField: 'whatText',
    markField: 'whatMark',
  },
  {
    key: 'guidance',
    headingKey: 'issueMgmt.content.guidance',
    fieldKey: 'issueMgmt.field.guidance',
    textField: 'guidanceText',
    markField: 'guidanceMark',
  },
  {
    key: 'fixed',
    headingKey: 'issueMgmt.content.fixed',
    fieldKey: 'issueMgmt.field.fixed',
    textField: 'fixedText',
    markField: 'fixedMark',
  },
] as const;

/**
 * 課題の中身3項目（何が起きているか・今の案内・直った状態）を横に並べる。
 * AI が書いた項目には「AI下書き」の札と「確かめた」を出す。編集キーの人は文を押すとその場で直せ、直すと札が消える。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の中身3項目を並べて見せ、AI下書きを確かめる・その場で直す操作を編集キーの人に出す
 * @graph-connects InlineTextEditor [calls] その場で直す
 * @graph-connects IssueText [calls] 伏せ字の札
 */
export function IssueContentCards({
  issue,
  canEdit,
  save,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  save: SaveIssueFields;
}) {
  const { t } = useTranslation();
  return (
    <section className="grid gap-px overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-border)] md:grid-cols-3">
      {CONTENT_ITEMS.map((item) => {
        const { text, mark } = issue.content[item.key];
        const field = t(item.fieldKey);
        const isDraft = mark === 'ai_draft' && text !== null;
        return (
          <div key={item.key} className="bg-[var(--color-bg-surface)] p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t(item.headingKey)}
              </h2>
              {isDraft && (
                <span title={t('issueMgmt.content.aiDraftHint')}>
                  <Badge variant="info">{t('issueMgmt.content.aiDraft')}</Badge>
                </span>
              )}
              {isDraft && canEdit && (
                <button
                  type="button"
                  className="text-xs font-medium text-[var(--color-accent-primary)] hover:underline"
                  onClick={() => {
                    void save(
                      [{ field: item.markField, base: mark, value: 'human' }],
                      t('issueMgmt.content.confirmed', { title: issue.title, field }),
                    );
                  }}
                >
                  {t('issueMgmt.content.confirm')}
                </button>
              )}
            </div>
            <InlineTextEditor
              label={t(item.headingKey)}
              value={text}
              max={ISSUE_TEXT_MAX_LENGTH}
              canEdit={canEdit}
              onSave={(next) =>
                save(
                  [{ field: item.textField, base: text, value: next }],
                  t('issueMgmt.content.saved', { title: issue.title, field }),
                )
              }
            >
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-secondary)]">
                {text === null ? (
                  <span className="text-[var(--color-text-muted)]">
                    {t('issueMgmt.content.empty')}
                  </span>
                ) : (
                  <IssueText text={text} />
                )}
              </p>
            </InlineTextEditor>
          </div>
        );
      })}
    </section>
  );
}
